import { useRef, useState, useEffect, lazy, Suspense, useCallback } from 'react'
import { Link } from 'react-router'
import { Navbar } from '@/components/layout/Navbar'
import { Footer } from '@/components/layout/Footer'
import { Button } from '@/components/ui/Button'
import { selectPublicAuthStatus, useAuthStore } from '@/stores/authStore'
import { PuzzleAssembly } from '@/components/three/PuzzleAssembly'
import { isMakerDirectoryEnabled } from '@/lib/makers'
import { HeroReviewTicker } from '@/components/landing/HeroReviewTicker'
import {
  HERO_TESTIMONIALS,
  SLOVAKIA_PHOTO,
  WALL_PHOTO,
  type ReviewPhoto,
} from '@/components/landing/testimonials'

// The subscribe funnel's named destination (D2/plan Phase 5): every primary
// "get started" CTA on the landing page points here first, not straight at a
// signup form — choosing a plan is the main event, the free trial is one tap
// away from it.
const PRICING_PATH = '/pricing'
// The open (codeless) free-trial signup — every "Start free" CTA points here
// rather than at /auth/register so a code redeemed earlier in the tab can't
// turn the click into a coded signup.
const SIGNUP_PATH = '/signup'

function onClickTrack(source: string) {
  window.t6track?.('landing_cta_clicked', { source })
}

const bandRevealImport = import('@/components/three/BandReveal')
const LazyBandReveal = lazy(() => bandRevealImport.then((m) => ({ default: m.BandReveal })))

function HeroSection({ isAuthenticated }: { isAuthenticated: boolean | null }) {
  const [modelReady, setModelReady] = useState(false)

  const handleModelReady = useCallback(() => {
    setModelReady(true)
  }, [])

  return (
    <section className="flex min-h-[calc(100dvh-3.5rem)] flex-col bg-white">
      <div className="relative w-full flex-1 overflow-hidden bg-white" aria-hidden="true">
        {/* Background / Fallback image */}
        {!modelReady && (
          <div className="absolute inset-0">
            <img src="/images/hero-banner.png?v=2" alt="" className="h-full w-full object-cover" />
          </div>
        )}

        {/* 3D Model */}
        <div className="absolute inset-0">
          <Suspense fallback={null}>
            <LazyBandReveal modelUrl="/models/terrain-draco.glb" onReady={handleModelReady} />
          </Suspense>
        </div>

        <div
          className="pointer-events-none absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 px-2 text-center whitespace-nowrap text-black"
          style={{
            fontFamily: 'GeistPixel, monospace',
            fontSize: 'clamp(40px, 10vw, 96px)',
            letterSpacing: '0.05em',
            WebkitFontSmoothing: 'none',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeSpeed',
          }}
        >
          Terrain&nbsp;6
        </div>
      </div>

      <div className="z-20 mx-auto flex w-full max-w-3xl shrink-0 flex-col items-center justify-start px-4 py-4 text-center sm:py-8">
        <p
          className="text-sage text-[11px] tracking-[0.2em] uppercase sm:text-xs"
          style={{ fontFamily: 'GeistPixel, monospace' }}
        >
          3D-printed topographic puzzles
        </p>
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-balance text-black sm:text-4xl lg:text-5xl">
          Your Trail. Your Terrain. Your Puzzle.
        </h1>
        <p className="mt-3 text-base text-gray-600 sm:mt-4 sm:text-lg lg:text-xl">
          Turn any place on Earth into a 3D-printed topographic puzzle. Trails, summits, lakes, your
          backyard — pick a spot and we transform real elevation data into a tactile piece you can
          hold.
        </p>
        <div className="mt-6 grid w-full max-w-md grid-cols-1 items-center gap-3 sm:mt-8 sm:flex sm:max-w-none sm:flex-wrap sm:justify-center sm:gap-4">
          {isAuthenticated === null ? (
            <span
              aria-label="Checking session"
              role="status"
              className="h-11 w-32 animate-pulse rounded-lg bg-gray-100"
            />
          ) : isAuthenticated ? (
            <Button variant="primary" to="/app" className="w-full sm:w-auto">
              Go to App
            </Button>
          ) : (
            <>
              {/* Subscribing is the main event — the pricing table is the
                  primary CTA everywhere on this page. */}
              <Button
                variant="primary"
                to={PRICING_PATH}
                onClick={() => onClickTrack('landing_hero_pricing')}
                className="w-full sm:w-auto"
              >
                Choose your plan
              </Button>
              <Button variant="secondary" to={SIGNUP_PATH} className="w-full sm:w-auto">
                Start free
              </Button>
            </>
          )}
        </div>
        {isAuthenticated === false && (
          <>
            <p className="mt-3 text-sm text-gray-500">
              5 free generations, no credit card. Upgrade whenever you're ready to download.
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/auth/login" className="text-sage font-medium hover:underline">
                Sign in
              </Link>
            </p>
            {/* For visitors who want a physical print rather than an account —
                deliberately quieter than the sign-up CTAs above. */}
            {isMakerDirectoryEnabled() && (
              <p className="mt-3 text-sm text-gray-500">
                Just want a print?{' '}
                <Link
                  to="/makers"
                  onClick={() => onClickTrack('landing_hero_find_a_maker')}
                  className="text-gray-600 underline-offset-2 hover:text-black hover:underline"
                >
                  Find a Maker →
                </Link>
              </p>
            )}
          </>
        )}
        <HeroReviewTicker testimonials={HERO_TESTIMONIALS} photosHref="#gallery" />
        <div className="mt-3 text-[12px] tracking-[0.1em] text-gray-400 uppercase">Scroll ↓</div>
      </div>
    </section>
  )
}

function PuzzleAssemblySection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const stepsColRef = useRef<HTMLDivElement>(null)
  const canvasFrameRef = useRef<HTMLDivElement>(null)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const progressRef = useRef(0)

  // Tailwind's sm breakpoint is 640px. We match the same breakpoint here so the
  // canvas-split rule (CSS top-half on mobile, side-by-side on desktop) tracks
  // the CSS layout exactly. SSR-safe: default to desktop on first render so
  // the camera doesn't briefly squeeze before hydration.
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639.98px)')
    const apply = () => setIsMobile(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const steps = [
    {
      title: 'Pick Your Trail',
      description:
        'Search thousands of trails across the US or browse by national park. Find the one that means something to you.',
    },
    {
      title: 'We Build Your Terrain',
      description:
        'Real LIDAR elevation data is transformed into a detailed 3D terrain model with your trail carved right in.',
    },
    {
      title: 'Download & Print',
      description:
        'Get production-ready files optimized for 3D printing. Assemble your puzzle and display your adventure.',
    },
  ]

  const stepThresholds = [0.05, 0.2, 0.35]

  useEffect(() => {
    function onScroll() {
      const section = sectionRef.current
      if (!section) return

      const rect = section.getBoundingClientRect()

      // Start the progress when the section's top is halfway down the viewport
      const startOffset = window.innerHeight * 0.5
      const scrollableDistance = section.offsetHeight - window.innerHeight + startOffset

      if (scrollableDistance <= 0) return

      const scrolled = startOffset - rect.top
      const progress = Math.max(0, Math.min(1, scrolled / scrollableDistance))
      progressRef.current = progress

      // Update layout directly on DOM — no React re-render needed
      const layoutT = Math.max(0, Math.min(1, (progress - 0.5) / 0.15))

      if (stepsColRef.current) {
        stepsColRef.current.style.opacity = String(Math.max(0, 1 - layoutT * 2))
        stepsColRef.current.style.pointerEvents = layoutT > 0.5 ? 'none' : ''
      }

      // On mobile the canvas always fills the full sticky area (so the model
      // ends centered) but is translated upward while the steps are showing
      // so the model sits in the top region above the steps. As layoutT
      // progresses (steps fade), the translation eases back to 0 and the
      // model drops down to the center of the full area. The shift amount
      // is derived from the actual steps height so short viewports (where
      // the steps need more room) push the model up further. On desktop no
      // translation is needed — the camera does the horizontal reveal.
      if (canvasFrameRef.current) {
        if (isMobile) {
          const stickyH = canvasFrameRef.current.parentElement?.clientHeight ?? 1
          const stepsH = stepsColRef.current?.clientHeight ?? 0
          // We want the canvas (full height) centered above the steps. The
          // natural center is at stickyH/2; the target center is at
          // (stickyH - stepsH)/2. Difference / stickyH gives the shift as
          // a fraction of the canvas height.
          const targetCenterY = (stickyH - stepsH) / 2
          const naturalCenterY = stickyH / 2
          const shiftPx = targetCenterY - naturalCenterY // negative — moves up
          const shiftPct = (shiftPx / stickyH) * 100 * (1 - layoutT)
          canvasFrameRef.current.style.transform = `translateY(${shiftPct}%)`
        } else {
          canvasFrameRef.current.style.transform = ''
        }
      }

      // Step reveal
      stepRefs.current.forEach((el, i) => {
        if (!el) return
        const active = progress >= stepThresholds[i]
        el.style.opacity = active ? '1' : '0.3'
        el.style.transform = active ? 'translateX(0)' : 'translateX(-20px)'
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [isMobile])

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="relative bg-black"
      style={{ height: '400vh' }}
    >
      <div className="sticky top-14 overflow-hidden" style={{ height: 'calc(100vh - 3.5rem)' }}>
        {/* Puzzle canvas frame. On mobile it occupies the top portion of the
            sticky area and grows to fill the full height as the scroll
            progresses (driven by --canvas-h, set by the scroll handler). On
            ≥sm it always fills the full sticky area and the camera handles
            the horizontal split internally. */}
        <div
          ref={canvasFrameRef}
          className="pointer-events-none absolute inset-0 z-0 bg-transparent transition-transform duration-200 ease-out will-change-transform"
        >
          <div className="h-full w-full sm:pointer-events-auto" style={{ touchAction: 'pan-y' }}>
            <PuzzleAssembly
              modelUrl="/models/puzzle-draco.glb"
              scrollProgressRef={progressRef}
              splitMode={isMobile ? 'full' : 'horizontal'}
            />
          </div>
        </div>

        {/* Steps column. On phones the steps sit in the bottom portion below
            the canvas (no overlay). On ≥sm the steps move into a 50% side
            column as before. */}
        <div
          ref={stepsColRef}
          className="absolute inset-x-0 bottom-0 z-10 flex flex-col justify-center gap-4 overflow-y-auto bg-black px-6 py-6 sm:inset-0 sm:right-auto sm:w-1/2 sm:gap-10 sm:overflow-visible sm:bg-transparent sm:px-8 sm:py-0"
          style={{ maxHeight: isMobile ? '65%' : undefined }}
        >
          {steps.map((step, i) => (
            <div
              key={step.title}
              ref={(el) => {
                stepRefs.current[i] = el
              }}
              style={{
                opacity: 0.3,
                transform: 'translateX(-20px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
              }}
            >
              <div className="flex items-center gap-3">
                <span className="bg-sage flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white">
                  {i + 1}
                </span>
                <h3 className="text-lg font-semibold text-white sm:text-xl">{step.title}</h3>
              </div>
              <p className="mt-2 pl-11 text-sm text-gray-300 sm:text-gray-400">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FeatureShowcase() {
  const features = [
    {
      title: 'Real Elevation Data',
      description: 'We use high-resolution LIDAR data to create accurate terrain models.',
    },
    {
      title: 'Trail-Carved Detail',
      description:
        'Your chosen trail is carved directly into the terrain surface for a tactile hiking experience.',
    },
    {
      title: 'Puzzle-Cut Pieces',
      description:
        'Each model is sliced into interlocking puzzle pieces that slide together perfectly.',
    },
    {
      title: 'Print-Ready Files',
      description: 'Optimized 3MF files for Bambu printers. No cleanup needed.',
    },
    {
      title: 'Any Size and Shape',
      description:
        'Square or rectangular: pick your piece size, from a 50 mm desk tile to a 1 m centrepiece.',
    },
    {
      title: 'Worldwide Coverage',
      description:
        'Search trails, summits, lakes, and parks across the globe — or drop a pin anywhere on Earth.',
    },
  ]

  return (
    <section id="about" className="bg-gray-50 py-24">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-center text-3xl font-bold text-black">What Makes T6 Special</h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-gray-600">
          Every puzzle is generated from real geospatial data, making each one as unique as the
          trail it represents.
        </p>
        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div key={feature.title} className="rounded-xl border border-gray-100 bg-white p-6">
              <h3 className="text-lg font-semibold text-black">{feature.title}</h3>
              <p className="mt-2 text-sm text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CustomerPrintTile({
  photo,
  quote,
  author,
  className,
  imgClassName,
}: {
  photo: ReviewPhoto
  quote: string
  author: string
  className: string
  imgClassName: string
}) {
  return (
    <figure className={`group relative overflow-hidden rounded-2xl ${className}`}>
      <img
        src={photo.src}
        alt={photo.alt}
        loading="lazy"
        className={`w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] ${imgClassName}`}
      />
      <span className="absolute top-3 left-3 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold tracking-wider text-black uppercase">
        Customer print
      </span>
      <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 pt-12 text-white">
        <blockquote className="text-sm leading-snug font-medium">“{quote}”</blockquote>
        <p className="mt-1 text-xs text-gray-300">
          <span aria-label="5 out of 5 stars">★★★★★</span> · {author}
        </p>
      </figcaption>
    </figure>
  )
}

function GallerySection() {
  const shots = [
    {
      src: '/images/gallery/DSC03641.webp',
      alt: 'Topographic puzzles in green, orange, white, and dark green filaments scattered across a desk with a hand placing a piece',
      caption: 'Made to be built, not just displayed',
    },
    {
      src: '/images/gallery/all-puzzles.webp',
      alt: 'Six topographic puzzles in different colors and styles arrayed on a desk — trails, rivers, and summits from around the world',
      caption: 'Trails, rivers, and summits — anywhere on Earth',
    },
    {
      src: '/images/gallery/homepage.webp',
      alt: 'Six finished puzzles displayed on their custom bases — each engraved with trail name, coordinates, and topo lines',
      caption: 'Custom bases — engraved with the name, coordinates, and topo lines',
    },
  ]

  return (
    <section id="gallery" className="bg-white py-24">
      <div className="mx-auto max-w-6xl px-4">
        <div className="max-w-2xl">
          <p className="text-sage text-xs tracking-[0.2em] uppercase">Printed &amp; Photographed</p>
          <h2 className="mt-3 text-3xl font-bold text-black sm:text-4xl">From data to desk.</h2>
          <p className="mt-4 text-gray-600">
            From real elevation data to a Bambu plate — no special equipment, no manual modeling.
          </p>
        </div>

        {/* 4×3 bento: our studio shots, with real 5-star customer prints
            (and one pull quote, first in its column so it's seen on scroll)
            woven in around them. */}
        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-4 md:grid-rows-[repeat(3,240px)]">
          <figure className="group relative overflow-hidden rounded-2xl md:col-span-2 md:row-span-2">
            <img
              src={shots[0].src}
              alt={shots[0].alt}
              loading="lazy"
              className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] md:h-full"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-6 text-sm text-white">
              {shots[0].caption}
            </figcaption>
          </figure>

          <figure className="bg-sage flex flex-col justify-between gap-6 rounded-2xl p-6 text-white md:col-start-3 md:row-start-1">
            <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white/60" aria-hidden="true">
              <path d="M7 7h4v4c0 3-1.5 5-4 6l-.8-1.4C7.6 14.8 8.3 13.6 8.4 12H7zm7 0h4v4c0 3-1.5 5-4 6l-.8-1.4c1.4-.8 2.1-2 2.2-3.6H14z" />
            </svg>
            <blockquote className="text-lg leading-snug font-semibold">
              “Once you load the file, it’s just a matter of picking your colors.”
            </blockquote>
            <figcaption className="text-sm text-white/85">Azline</figcaption>
          </figure>

          <figure className="group relative overflow-hidden rounded-2xl md:col-start-3 md:row-start-2">
            <img
              src={shots[1].src}
              alt={shots[1].alt}
              loading="lazy"
              className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] md:h-full"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4 text-xs text-white">
              {shots[1].caption}
            </figcaption>
          </figure>

          <CustomerPrintTile
            photo={WALL_PHOTO}
            quote="Every model I’ve downloaded has printed without an issue."
            author="Azline"
            className="md:col-start-4 md:row-span-3 md:row-start-1"
            imgClassName="h-[28rem] md:h-full"
          />

          <figure className="group relative overflow-hidden rounded-2xl md:col-start-3 md:row-start-3">
            <img
              src={shots[2].src}
              alt={shots[2].alt}
              loading="lazy"
              className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] md:h-full"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4 text-xs text-white">
              {shots[2].caption}
            </figcaption>
          </figure>

          <CustomerPrintTile
            photo={SLOVAKIA_PHOTO}
            quote="Terrain 6 is perfect."
            author="Beta member"
            className="md:col-span-2 md:row-start-3"
            imgClassName="h-64 md:h-full"
          />
        </div>
      </div>
    </section>
  )
}

function CTASection({ isAuthenticated }: { isAuthenticated: boolean | null }) {
  return (
    <section id="get-started" className="py-24">
      <div className="mx-auto max-w-2xl px-4 text-center">
        <h2 className="text-3xl font-bold text-black">Ready to Hold Your Adventure?</h2>
        <p className="mt-4 text-lg text-gray-600">
          Pick a plan and start turning your favorite places into stunning 3D puzzles today.
        </p>
        <div className="mt-8 flex items-center justify-center gap-4">
          {isAuthenticated === null ? (
            <span
              aria-label="Checking session"
              role="status"
              className="h-11 w-32 animate-pulse rounded-lg bg-gray-100"
            />
          ) : isAuthenticated ? (
            <Button variant="primary" to="/app">
              Go to App
            </Button>
          ) : (
            <>
              <Button
                variant="primary"
                to={PRICING_PATH}
                onClick={() => onClickTrack('landing_footer_pricing')}
              >
                Choose your plan
              </Button>
              <Button variant="secondary" to={SIGNUP_PATH}>
                Start free
              </Button>
            </>
          )}
        </div>
        {isAuthenticated === false && (
          <>
            <p className="mt-3 text-sm text-gray-500">
              5 free generations, no credit card. Upgrade whenever you're ready to download.
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/auth/login" className="text-sage font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </>
        )}
        {isMakerDirectoryEnabled() && (
          <p className="mt-8 text-sm text-gray-600">
            Don&apos;t have a printer?{' '}
            <Link
              to="/makers"
              onClick={() => onClickTrack('landing_find_a_maker')}
              className="text-sage font-medium hover:underline"
            >
              Find a Maker to print it for you →
            </Link>
          </p>
        )}
      </div>
    </section>
  )
}

export default function LandingPage() {
  const isAuthenticated = useAuthStore(selectPublicAuthStatus)

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar variant="landing" />
      <main className="flex-1">
        <HeroSection isAuthenticated={isAuthenticated} />
        <PuzzleAssemblySection />
        <GallerySection />
        <FeatureShowcase />
        <CTASection isAuthenticated={isAuthenticated} />
      </main>
      <Footer />
    </div>
  )
}
